import React from 'react';
import { Tab as AriaTab } from 'react-aria-components';
import { Heading } from '../Heading/Heading';
import Icon from '../Icon/Icon';
import { useTabsContext } from './Tabs';
import { focusStyleVariants } from '../../utils/focus/focusStyles';
import styles from './Tabs.module.css';

export interface TabProps {
  /**
   * Unique identifier for the tab
   */
  id: string;
  /**
   * Tab label content
   */
  children: React.ReactNode;
  /**
   * Icon name to display in the tab
   */
  iconName?: string;
  /**
   * URL to navigate to when tab is clicked (for link tabs)
   */
  href?: string;
  /**
   * Whether the tab is disabled
   */
  isDisabled?: boolean;
  /**
   * Additional CSS class names
   * @ignore
   */
  className?: string;
  /**
   * Test ID for the component, used for testing purposes
   */
  'data-testid'?: string;
}

export const Tab: React.FC<TabProps> = ({
  id,
  children,
  iconName,
  href,
  isDisabled,
  className,
  'data-testid': dataTestId,
}) => {
  const context = useTabsContext();
  const { styleVariant, focusStyle, headingLevel } = context;

  const renderTabContent = () => {
    const iconSize = styleVariant === 'heading' ? '1.5x' : '1x';

    const content = (
      <>
        {iconName && (
          <Icon
            iconName={iconName}
            iconSize={iconSize}
            padding="square"
            aria-hidden="true"
          />
        )}
        {children}
      </>
    );

    if (styleVariant === 'heading') {
      return (
        <Heading as={headingLevel} size="sm">
          {content}
        </Heading>
      );
    }

    return content;
  };

  const combinedClassName = className
    ? `${styles.tabs__tab} ${focusStyleVariants({ focusStyle })} ${className}`
    : `${styles.tabs__tab} ${focusStyleVariants({ focusStyle })}`;

  return (
    <AriaTab
      id={id}
      href={href}
      className={combinedClassName}
      data-has-icon={iconName ? 'true' : undefined}
      data-focus-style={focusStyle === 'white' ? 'white' : undefined}
      isDisabled={isDisabled}
      data-testid={dataTestId}
    >
      <span className={styles['tabs__tab-text']}>{renderTabContent()}</span>
    </AriaTab>
  );
};

export default Tab;
